import {createRow, getMemoOptions, memo, type Row, type RowModel, type Table} from '@tanstack/react-table'

import type {Group} from '../../../api/memex-items/paginated-views'
import {getRowId, groupedRowId} from '../../../components/react_table/get-row-id'
import {getGroupingMetadataFromServerGroupValue} from '../../../features/grouping/get-grouping-metadata'
import type {FieldGrouping} from '../../../features/grouping/types'
import type {MemexItemModel} from '../../../models/memex-item-model'
import type {usePaginatedMemexItemsQueryReturnType} from '../../../state-providers/memex-items/queries/use-paginated-memex-items-query'

/**
 * Custom GroupedRowModel that reads pre-grouped items data from the query cache
 * and formats it for use by `useReactTable`.
 *
 * @link [Customizing RowModels docs](https://tanstack.com/table/latest/docs/guide/row-models#customizefork-row-models)
 * @link [TanStack `getGroupedRowModel` source](https://github.com/TanStack/table/blob/9c62cf2024b86c419f7015f963248739bab1b0e7/packages/table-core/src/utils/getGroupedRowModel.ts)
 */
export function getCustomGroupedRowModel(): (table: Table<MemexItemModel>) => () => RowModel<MemexItemModel> {
  return table => {
    return memo(
      () => [
        table.getState().grouping,
        table.getPreGroupedRowModel(),
        table.getState().groupQueryData,
        table.getAllColumns(),
      ],
      (grouping, rowModel, queryData, columns) => {
        const {groupedItemQueries, queriesForGroups, groupsById} = queryData
        const groupedByColumn = columns.find(col => col.id === grouping[0])?.columnDef.meta?.columnModel
        if (!groupedItemQueries || !groupedByColumn) {
          for (const row of rowModel.rows) {
            row.depth = 0
            row.parentId = undefined
          }
          return rowModel
        }

        // Table view currently supports one layer of grouping
        const groupByDepth = 0
        // Track the relative position of an item row within the full table
        let itemsIndex = 0

        const groupedRowsById: Record<string, Row<MemexItemModel>> = {}
        const groupedFlatRows: Array<Row<MemexItemModel>> = []
        const groupedRows = queriesForGroups
          .flatMap(query => query?.data?.groups || [])
          .map((groupQueryData, index) => {
            const {groupId} = groupQueryData
            const itemQueries = groupedItemQueries[groupId]
            const groupQuery = groupsById[groupId]

            if (!groupQuery || !itemQueries) {
              // There isn't a known scenario where this should be the case - the
              // groupedItemQueries and groupsById should be updated in lock step
              // so we should always be able to look up a group by its groupId.
              // However, if we do encounter a scenario where we're unable to find the group
              // we will just return undefined here and then filter out the null values
              // after we've mapped over all of the entries.
              return undefined
            }

            const getSubRows = (parentId: string, depth: number) => {
              const items = itemQueries.reduce((all, curr) => {
                all.push(...(curr?.data?.nodes || []))
                return all
              }, [] as Array<MemexItemModel>)

              return items.map(item => {
                // An itemRow's index should always be relative to all item rows in the table, whether or not
                // grouping is applied.
                // Memex uses this index to calculate the row number displayed by the leading RowDragHandle column.
                const rowId = getRowId(item, itemsIndex, {id: parentId})
                const itemRow = createRow<MemexItemModel>(table, rowId, item, itemsIndex, depth, undefined, parentId)
                groupedFlatRows.push(itemRow)
                groupedRowsById[itemRow.id] = itemRow
                itemsIndex += 1
                return itemRow
              })
            }

            const groupRowId = groupedRowId(groupedByColumn.id, groupId)
            const subRows = getSubRows(groupRowId, groupByDepth + 1)
            const {groupValue, groupMetadata, totalCount} = groupQuery
            const groupedSourceObject = getGroupingMetadataFromServerGroupValue(
              groupedByColumn,
              groupValue,
              groupMetadata,
            )
            if (!groupedSourceObject) return
            /**
             * Use the first item to build the grouped row for consistency with native behavior:
             * "If the row is a grouped row, the original row object will be the first original in the group."
             * @link [API Docs](https://tanstack.com/table/latest/docs/api/core/row#original)
             */
            const firstItem = subRows[0]?.original
            if (!firstItem) return
            // Omitting CustomGroupingRow properties here forces helpful
            // type errors if a property is omitted when building groupedRow below.
            const baseRow: Omit<Row<MemexItemModel>, keyof CustomGroupingRow> = createRow<MemexItemModel>(
              table,
              groupRowId,
              firstItem,
              index,
              groupByDepth,
            )
            const groupedRow: Row<MemexItemModel> = Object.assign(baseRow, {
              groupingColumnId: groupedByColumn.id.toLocaleString(),
              groupingValue: groupId,
              subRows,
              // Custom prop
              groupQueryData: groupQuery,
              // Deprecated props for backwards-compatibility
              groupedValue: groupId,
              groupedSourceObject,
              totalCount: totalCount.value,
            })
            groupedFlatRows.push(groupedRow)
            groupedRowsById[groupedRow.id] = groupedRow
            return groupedRow
          })
          .filter(r => !!r)

        return {
          /**
           * Only includes parent group rows.
           */
          rows: groupedRows,
          /**
           * Includes both parent group rows and leaf item rows.
           * Use row.getIsGrouped() to determine if a row is a parent group row.
           */
          flatRows: groupedFlatRows,
          /**
           * Includes both parent group rows and leaf item rows.
           * Use row.getIsGrouped() to determine if a row is a parent group row.
           */
          rowsById: groupedRowsById,
        }
      },
      getMemoOptions(table.options, 'debugTable', 'getGroupedRowModel', () => {
        /**
         * TanStack Table queues callbacks to reset the table after a state update.
         * Memex doesn't currently rely on any features that require queuing events,
         * but the below is left to document the entry point from the source code.
         * @link [source](https://github.com/TanStack/table/blob/9c62cf2024b86c419f7015f963248739bab1b0e7/packages/table-core/src/utils/getGroupedRowModel.ts#L169-L172)
         * @link [_queue references](https://github.com/search?q=repo:TanStack/table%20_queue&type=code)
         */
        // table._queue(() => {})
      }),
    )
  }
}

export type DeprecatedGroupedRowProps = {
  /** @deprecated Use `groupingValue` instead */
  groupedValue: string
  /** @deprecated Use `groupQueryData.groupMetadata` instead */
  groupedSourceObject: FieldGrouping
  /** @deprecated Use `groupQueryData.totalCount` instead */
  totalCount: number
}

/**
 * Extends TableState interface to track group data from the query cache
 */
type QueryDataState = Pick<
  usePaginatedMemexItemsQueryReturnType,
  'groupedItemQueries' | 'queriesForGroups' | 'groupsById'
>
interface QueryDataTableState {
  groupQueryData: QueryDataState
}

/**
 * Extends GroupingRow interface with additional group properties
 * @link [GroupingRow source](https://github.com/TanStack/table/blob/9c62cf2024b86c419f7015f963248739bab1b0e7/packages/table-core/src/features/ColumnGrouping.ts#L111)
 */
type CustomGroupingRow = {
  groupQueryData: Omit<Group, 'fieldMetrics'>
} & DeprecatedGroupedRowProps

/**
 * @link [Declaration merging documentation](https://tanstack.com/table/latest/docs/guide/custom-features#step-2-use-declaration-merging-to-add-new-types-to-tanstack-table)
 */
declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  interface GroupingRow extends CustomGroupingRow {}
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  interface TableState extends QueryDataTableState {}
}
